import React, { useState } from 'react';
import { MapPin, Mail, Phone, CheckCircle, Send, Clock, AlertCircle, Navigation } from 'lucide-react';
const STORE = {
    placeUrl:
        'https://www.google.com/maps/place/Laundry+To,+DF+05,+Street+Number+316,+behind+DF+MARKET,+DF+Block(Newtown),+Action+Area+I,+Newtown,+KOLKATA,+New+Town,+West+Bengal+700156/@22.5958,88.4799,17z',
    directionsUrl:
        'https://maps.app.goo.gl/wzb5BiWEBuxwm2c86',
};

const EMBED_SRC =
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3684.0!2d88.4799!3d22.5958!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f17.1!3m3!1m2!1s0x3a02752102de9483%3A0x31465060c5b5e4ca!2sLaundry+To%2C+DF+05%2C+Street+Number+316%2C+DF+Block%2C+New+Town%2C+West+Bengal+700156!5e0!3m2!1sen!2sin!4v1711272356175!5m2!1sen!2sin';
const ContactInfo = ({ icon, title, info }) => {
    return (
        <div className="flex items-start bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow text-left">
            <div className="p-3 bg-neutral-100 rounded-full mr-4 text-green-700">
                {icon}
            </div>
            <div>
                <h3 className="font-bold text-gray-800 mb-1">{title}</h3>
                <p className="text-gray-600">{info}</p>
            </div>
        </div>
    );
};

const SocialIcon = ({ href, children, label }) => {
    return (
        <a
            href={href}
            className="p-3 bg-neutral-100 text-green-700 rounded-full hover:bg-neutral-500 hover:text-white transition-colors flex items-center justify-center group relative"
            aria-label={label}
            target="_blank"
            rel="noopener noreferrer"
        >
            {children}
            <span className="absolute top-full mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                {label}
            </span>
        </a>
    );
};

const FormField = ({ label, id, type = "text", placeholder, value, onChange, error }) => {
    return (
        <div className="mb-6">
            <label htmlFor={id} className="block text-gray-700 font-medium mb-2">{label}</label>
            {type === "textarea" ? (
                <textarea
                    id={id}
                    rows="5"
                    className={`w-full px-4 py-3 border ${error ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500`}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                ></textarea>
            ) : (
                <input
                    type={type}
                    id={id}
                    className={`w-full px-4 py-3 border ${error ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500`}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                />
            )}
            {error && (
                <p className="mt-1 text-red-500 text-sm flex items-center">
                    <AlertCircle size={14} className="mr-1" /> {error}
                </p>
            )}
        </div>
    );
};

const Contact = () => {
    const [formState, setFormState] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormState(prev => ({
            ...prev,
            [id]: value
        }));

        // Clear error when user starts typing
        if (errors[id]) {
            setErrors(prev => ({
                ...prev,
                [id]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formState.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formState.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formState.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formState.subject.trim()) {
            newErrors.subject = 'Subject is required';
        }

        if (!formState.message.trim()) {
            newErrors.message = 'Message is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            setIsSubmitting(true);

            // Simulate API call
            setTimeout(() => {
                setIsSubmitting(false);
                setIsSubmitted(true);
                setFormState({
                    name: '',
                    email: '',
                    phone: '',
                    subject: '',
                    message: ''
                });
            }, 1500);
        }
    };

    const officeHours = [
        { day: 'Monday - Friday', hours: '8:00 AM - 8:00 PM' },
        { day: 'Saturday', hours: '9:00 AM - 6:00 PM' },
        { day: 'Sunday', hours: '10:00 AM - 2:00 PM' }
    ];

    return (
        <section id="contact" className="py-16 md:py-24 bg-gray-50">
            <div className="container mx-auto px-4">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <span className="inline-block px-4 py-2 bg-neutral-100 text-neutral-700 rounded-full text-sm font-medium mb-4">Get In Touch</span>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Contact <span className="text-green-600">Urban</span><span className="text-gray-900">Shine</span></h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Have questions about our laundry services? Want to schedule a pickup? We're here to help you with all your laundry needs.
                    </p>
                </div>

                {/* Contact Information Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
    <a href={STORE.placeUrl} target="_blank" rel="noopener noreferrer" className="block">
        <ContactInfo
            icon={<MapPin size={24} />}
            title="Visit Our Store"
            info="DF 05, Street Number 316, DF Block, New Town, Kolkata 700156"
        />
    </a>

    <ContactInfo
        icon={<Mail size={24} />}
                        title="Email Us"
                        info="urbanshinelaundry46@gmail.com"
                    />

                    <ContactInfo
                        icon={<Phone size={24} />}
                        title="Call Us"
                        info="+91 84509 03882"
                    />
                </div>

                {/* Map */}
                {/* Map */}
                <div className="relative w-full h-96 rounded-lg overflow-hidden">
                    <iframe
                        src={EMBED_SRC}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Urban Shine Location"
                        className="rounded-lg"
                    ></iframe>

                    {/* Button overlaid on map — bottom right corner */}
                    <div className="absolute bottom-4 right-4">
                        
                           <a href={STORE.directionsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-lg transition-all group"
                        >
                            <Navigation size={18} className="transition-transform duration-300 group-hover:-translate-y-1 group-hover:scale-110" />
                            Get Directions
                        </a>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default Contact;